import { httpClient } from "@bw/shared/api";
import type { BanResponse, Schema } from "@bw/shared/types/generated";

export type PlatformUser = Omit<Schema["PlatformUser"], "roles"> & {
  roles: NonNullable<Schema["PlatformUser"]["roles"]>;
};

export type PlatformUserOrgMembership = Omit<
  Schema["PlatformUserOrgMembership"],
  "roles"
> & {
  roles: NonNullable<Schema["PlatformUserOrgMembership"]["roles"]>;
};

export type PlatformUserActivitySummary = Schema["PlatformUserActivitySummary"];

export type PlatformUserDetail = Omit<
  Schema["PlatformUserDetailResponse"],
  | "activity_summary"
  | "ban_history"
  | "org_memberships"
  | "recent_activity"
  | "roles"
> & {
  activity_summary: PlatformUserActivitySummary;
  ban_history: NonNullable<Schema["PlatformUserDetailResponse"]["ban_history"]>;
  org_memberships: PlatformUserOrgMembership[];
  recent_activity: NonNullable<
    Schema["PlatformUserDetailResponse"]["recent_activity"]
  >;
  roles: NonNullable<Schema["PlatformUserDetailResponse"]["roles"]>;
};

export type PlatformUserListResponse = Omit<
  Schema["PlatformUserListResponse"],
  "items"
> & {
  items: PlatformUser[];
};

export interface ListUsersParams {
  search?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

function normalizeUser(user: Schema["PlatformUser"]): PlatformUser {
  return {
    ...user,
    roles: user.roles ?? [],
  };
}

function normalizeOrgMembership(
  membership: Schema["PlatformUserOrgMembership"],
): PlatformUserOrgMembership {
  return {
    ...membership,
    roles: membership.roles ?? [],
  };
}

function normalizeUserDetail(
  user: Schema["PlatformUserDetailResponse"],
): PlatformUserDetail {
  return {
    ...user,
    roles: user.roles ?? [],
    org_memberships: (user.org_memberships ?? []).map(normalizeOrgMembership),
    ban_history: user.ban_history ?? [],
    recent_activity: user.recent_activity ?? [],
    activity_summary: user.activity_summary ?? {
      last_active_at: null,
      recent_audit_count: 0,
      reports_filed_count: 0,
      reports_against_count: 0,
    },
  };
}

export async function listUsers(
  params?: ListUsersParams,
): Promise<PlatformUserListResponse> {
  const sp = new URLSearchParams();
  if (params?.search) sp.set("search", params.search);
  if (params?.status) sp.set("status", params.status);
  if (params?.limit !== undefined) sp.set("limit", String(params.limit));
  if (params?.offset !== undefined) sp.set("offset", String(params.offset));
  const qs = sp.toString();
  const response = await httpClient.get<Schema["PlatformUserListResponse"]>(
    `/platform/users${qs ? `?${qs}` : ""}`,
  );
  return {
    ...response,
    items: response.items.map(normalizeUser),
  };
}

export async function getUser(userId: string): Promise<PlatformUserDetail> {
  const response = await httpClient.get<Schema["PlatformUserDetailResponse"]>(
    `/platform/users/${encodeURIComponent(userId)}`,
  );
  return normalizeUserDetail(response);
}

export async function banUser(
  userId: string,
  reason: string,
  expiresAt?: string | null,
): Promise<BanResponse> {
  const payload = {
    user_id: userId,
    reason,
    ...(expiresAt ? { expires_at: expiresAt } : {}),
  } satisfies Schema["BanUserRequest"];

  return httpClient.post<BanResponse>("/moderation/bans", payload);
}

export type EligibilityCorrection = Schema["EligibilityCorrectionRequest"];

/** Support correction of a player's locked date of birth or gender (audited). */
export async function correctEligibility(
  userId: string,
  correction: EligibilityCorrection,
): Promise<Schema["UserProfileResponse"]> {
  return httpClient.post<Schema["UserProfileResponse"]>(
    `/platform/users/${encodeURIComponent(userId)}/eligibility-correction`,
    correction,
  );
}

export async function unbanUser(userId: string): Promise<void> {
  const payload = { user_id: userId } satisfies Schema["UnbanUserRequest"];
  await httpClient.post("/moderation/bans/unban", payload);
}

export async function sendPasswordReset(email: string): Promise<void> {
  const payload = { email } satisfies Schema["PasswordResetRequest"];
  await httpClient.post<void>("/users/auth/reset-password", payload);
}
